import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { setAudienceOverride, useAudienceSettings } from "../lib/useAudienceSettings";
import { api } from "../lib/api";

/**
 * iter132 — View Selector bottom sheet (refactored per spec).
 *
 * • Cards stack vertically, full width.
 * • No icons, no checkmark circles — the WHOLE card border highlights.
 * • Selected: 2px solid #E8B84B, background rgba(232,184,75,0.05),
 *   title color #E8B84B.
 * • Unselected: 2px solid #1f1f1f, background #141414, title #F5F1E8.
 * • Subtitle 14px DM Sans, "You can change this anytime" /
 *   "Puedes cambiar esto en cualquier momento".
 * • Save button fixed to the sheet bottom — always visible.
 */
export default function ViewSelectorSheet({ open, onClose, user }) {
  const { t } = useI18n();
  const { cohort } = useAudienceSettings();
  const initial = cohort === "local" ? "local" : "tourist";
  const [pending, setPending] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setPending(initial); }, [open, initial]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    setAudienceOverride(pending);
    if (user) {
      try { await api.post("/me/view-preference", { view: pending }); } catch { /* ignore */ }
    }
    if (typeof window !== "undefined") window.location.reload();
  };

  return (
    <div
      data-testid="view-selector-overlay"
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="view-selector-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md flex flex-col relative"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          maxHeight: "92vh",
        }}
      >
        <div className="mx-auto mt-3" style={{ width: 32, height: 3, background: "#2a2a2a", borderRadius: 2 }} />

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="view-selector-close"
          className="absolute right-3 top-3 grid place-items-center transition-colors hover:bg-white/[0.04] rounded-full"
          style={{ width: 36, height: 36, color: "#E8B84B" }}
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <header className="px-5 mt-3 text-center">
          <h2
            data-testid="view-selector-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}
          >
            {t("view_selector_title")}
          </h2>
          <p
            className="mt-1"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }}
          >
            {t("view_selector_sub")}
          </p>
        </header>

        <div className="px-5 mt-5 pb-4 space-y-3 overflow-y-auto" style={{ flex: "1 1 auto" }}>
          <ViewCard
            kind="tourist"
            title={t("view_tourist_title")}
            desc={t("view_tourist_desc")}
            selected={pending === "tourist"}
            onSelect={() => setPending("tourist")}
          />
          <ViewCard
            kind="local"
            title={t("view_local_title")}
            desc={t("view_local_desc")}
            selected={pending === "local"}
            onSelect={() => setPending("local")}
          />
        </div>

        <div
          className="px-5 pb-5 pt-3"
          style={{ background: "#171717", borderTop: "1px solid rgba(255,255,255,0.04)" }}
        >
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="view-selector-save"
            className="w-full transition-opacity disabled:opacity-50"
            style={{
              height: 52,
              borderRadius: 12,
              background: "linear-gradient(135deg, #E8B84B 0%, #B58E3D 100%)",
              color: "#1a0f06",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.02em",
              border: "none",
            }}
          >
            {pending === "tourist" ? t("view_save_tourist") : t("view_save_local")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewCard({ kind, title, desc, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`view-card-${kind}`}
      aria-pressed={selected}
      className="text-left w-full transition-colors"
      style={{
        padding: "18px 18px 16px",
        background: selected ? "rgba(232,184,75,0.05)" : "#141414",
        border: selected ? "2px solid #E8B84B" : "2px solid #1f1f1f",
        borderRadius: 8,
      }}
    >
      <h3
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 20,
          color: selected ? "#E8B84B" : "#F5F1E8",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h3>
      <p
        className="mt-2"
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490", lineHeight: 1.5 }}
      >
        {desc}
      </p>
    </button>
  );
}
